import { useState } from 'react';
import type { ChannelWithMembership } from '@avslack/shared';
import { useUpdateChannel } from '../data/channels';
import { Dialog, useAnimatedClose } from './Dialog';
import { Icon } from './Icon';

/**
 * Csatorna-beallitasok.
 *
 * Eddig a csatorna nevet, temajat es lathatosagat csak a LETREHOZASKOR
 * lehetett megadni - utolag semmit. Egy elgepelt nev vagy egy rossz
 * lathatosag igy csak ujra letrehozassal volt javithato, ami az elozmenyt is
 * elvitte.
 *
 * A jogosultsagot a SZERVER ellenorzi (`requireManageAccess`): a csatorna
 * letrehozoja vagy egy admin. A felulet ezt csak koveti - a gomb elrejtese
 * kenyelem, nem vedelem.
 */
export function ChannelSettingsDialog({
  channel,
  onClose: closeNow,
}: {
  channel: ChannelWithMembership;
  onClose: () => void;
}) {
  const onClose = useAnimatedClose(closeNow);
  const update = useUpdateChannel();

  const [name, setName] = useState(channel.name ?? '');
  const [topic, setTopic] = useState(channel.topic ?? '');
  const [purpose, setPurpose] = useState(channel.purpose ?? '');
  const [isPrivate, setPrivate] = useState(channel.kind === 'private');
  const [adminsOnly, setAdminsOnly] = useState(channel.postPolicy === 'admins');
  const [error, setError] = useState<string | null>(null);

  /** A lathatosag valtasa visszamenoleges - ezert kulon megerositest ker. */
  const goingPublic = channel.kind === 'private' && !isPrivate;

  const problem = !name.trim() ? 'A névnek nem szabad üresnek lennie.' : null;

  const save = async () => {
    if (problem) return;
    setError(null);
    try {
      await update.mutateAsync({
        channelId: channel.id,
        patch: {
          name: name.trim(),
          topic: topic.trim() || null,
          purpose: purpose.trim() || null,
          kind: isPrivate ? 'private' : 'public',
          postPolicy: adminsOnly ? 'admins' : 'all',
        },
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'A mentés nem sikerült.');
    }
  };

  const archive = async (next: boolean) => {
    setError(null);
    try {
      await update.mutateAsync({ channelId: channel.id, patch: { isArchived: next } });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'A művelet nem sikerült.');
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => !next && onClose()}
      title="Csatorna beállításai"
      description={`#${channel.name}`}
    >
      <label className="block">
        <span className="text-2xs font-semibold uppercase tracking-wide text-ink-faint">Név</span>
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          className="mt-1.5 w-full rounded-control border border-border bg-surface-sunken px-3 py-2 text-sm"
        />
      </label>

      <label className="mt-3 block">
        <span className="text-2xs font-semibold uppercase tracking-wide text-ink-faint">Téma</span>
        <input
          value={topic}
          onChange={(event) => setTopic(event.target.value)}
          placeholder="Egy sorban: miről szól ez a csatorna?"
          className="mt-1.5 w-full rounded-control border border-border bg-surface-sunken px-3 py-2 text-sm"
        />
      </label>

      <label className="mt-3 block">
        <span className="text-2xs font-semibold uppercase tracking-wide text-ink-faint">
          Leírás
        </span>
        <textarea
          value={purpose}
          onChange={(event) => setPurpose(event.target.value)}
          rows={2}
          placeholder="Hosszabban: kinek és mire való…"
          className="mt-1.5 w-full resize-none rounded-control border border-border bg-surface-sunken px-3 py-2 text-sm"
        />
      </label>

      {/* ─── Lathatosag ──────────────────────────────────────────────────── */}
      <div className="mt-4 rounded-card border border-border-soft p-3">
        <span className="text-2xs font-semibold uppercase tracking-wide text-ink-faint">
          Láthatóság
        </span>

        <label className="mt-2 flex cursor-pointer items-start gap-2.5">
          <input
            type="radio"
            name="channel-visibility"
            checked={isPrivate}
            onChange={() => setPrivate(true)}
            className="mt-1 accent-[var(--accent)]"
          />
          <span>
            <span className="flex items-center gap-1.5 text-sm font-medium">
              <Icon name="lakat" size="sm" /> Privát
            </span>
            <span className="block text-xs text-ink-muted">
              Csak a tagok látják. Belépni nem lehet – tagot admin vesz fel.
            </span>
          </span>
        </label>

        <label className="mt-2 flex cursor-pointer items-start gap-2.5">
          <input
            type="radio"
            name="channel-visibility"
            checked={!isPrivate}
            onChange={() => setPrivate(false)}
            className="mt-1 accent-[var(--accent)]"
          />
          <span>
            <span className="flex items-center gap-1.5 text-sm font-medium">
              <Icon name="csatorna" size="sm" /> Nyilvános
            </span>
            <span className="block text-xs text-ink-muted">
              A cég bármely tagja elolvashatja – akkor is, ha nem tagja.
            </span>
          </span>
        </label>

        {/*
          A NYILVANOSSA TETEL visszamenoleges.

          Nem csak a jovobeli uzenetek lesznek lathatoak: a teljes eddigi
          beszelgetes is. Aki eddig itt irt, abban a hitben tette, hogy csak a
          tagok latjak - ezt ki kell mondani, mielott ravaltana.
        */}
        {goingPublic && (
          <p className="mt-2 rounded-control border border-alert/30 bg-alert-soft px-2.5 py-2 text-xs text-alert">
            Ezzel a <strong>korábbi üzenetek is</strong> láthatóvá válnak mindenkinek – nem csak az
            ezután írtak. A csatornába rendszerüzenet kerül róla.
          </p>
        )}
      </div>

      <label className="mt-3 flex cursor-pointer items-start gap-2.5">
        <input
          type="checkbox"
          checked={adminsOnly}
          onChange={(event) => setAdminsOnly(event.target.checked)}
          className="mt-1 accent-[var(--accent)]"
        />
        <span>
          <span className="text-sm font-medium">Csak adminok írhatnak</span>
          <span className="block text-xs text-ink-muted">
            Bejelentésekhez. A többiek olvashatják és reagálhatnak rá.
          </span>
        </span>
      </label>

      {(problem || error) && (
        <p role="alert" className="mt-3 text-xs text-danger">
          {error ?? problem}
        </p>
      )}

      <div className="mt-5 flex items-center justify-between gap-2">
        {/*
          Az ARCHIVALAS kulon all a mentestol: nem beallitas, hanem dontes a
          csatorna eleterol. A "Mentes" melle keverve konnyu lenne veletlenul
          eltalalni.
        */}
        <button
          type="button"
          onClick={() => void archive(!channel.isArchived)}
          disabled={update.isPending}
          className="rounded-control px-2.5 py-2 text-xs text-ink-muted underline hover:text-ink"
        >
          {channel.isArchived ? 'Archiválás visszavonása' : 'Csatorna archiválása'}
        </button>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-control px-3 py-2 text-sm text-ink-muted hover:text-ink"
          >
            Mégse
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={Boolean(problem) || update.isPending}
            className="rounded-control bg-accent px-4 py-2 text-sm font-semibold text-accent-ink disabled:opacity-50"
          >
            {update.isPending ? 'Mentés…' : 'Mentés'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
